// The record engine: the lifecycle, stamps and offline-aware writes every module's records share.
// See docs/RECORD-STANDARD.md. Modules compose these; they do not re-implement them.
import { collection, deleteDoc, doc, getDocs, serverTimestamp, updateDoc, Timestamp, type DocumentReference } from 'firebase/firestore';
import { db } from '../firebase';
import { getPosition, type Pos } from '../geo';

export type RecordStatus = 'draft' | 'filed' | 'void';
export type RecordState = 'draft' | 'filed' | 'sent' | 'void';

/** The fields every record carries, whatever else the module adds. */
export interface BaseRecord {
  id: string;
  status: RecordStatus;
  createdBy: string;
  date: string;                      // local YYYY-MM-DD
  app?: 'v1' | 'v2';
  number?: string | null;
  location?: Pos | null;
  filedAt?: unknown; filedAtDevice?: unknown;
  sentTo?: string | null; sentAt?: unknown;
  voidedAt?: unknown; voidedBy?: { uid: string; name: string } | null; voidReason?: string | null;
  // Who did what (30-day list row 17): the stamp a page shows, the Sent block's facts. Optional
  // because v1 records never carried them; v2 adds them and never renames the old fields.
  createdByName?: string | null; createdAt?: unknown;
  filedBy?: string | null; filedByName?: string | null;
  updatedAt?: unknown; updatedByName?: string | null;
  sentBy?: string | null; sentByName?: string | null; sentEntryId?: string | null; sentFiles?: number | null;
}

export function stateOf(r: Pick<BaseRecord, 'status' | 'sentTo'> & { app?: string }): RecordState {
  if (r.status === 'void') return 'void';
  if (r.status === 'filed' || !r.app || r.app === 'v1') return r.sentTo ? 'sent' : 'filed';   // v1 records are filed by definition
  return 'draft';
}

export const STATE_LABEL: Record<RecordState, string> = { draft: 'Draft', filed: 'Filed', sent: 'Filed · sent', void: 'Void' };
export const STATE_PILL: Record<RecordState, string> = { draft: 'pill-warn', filed: 'pill-ok', sent: 'pill-ok', void: 'pill-crit' };

const SEND_WAIT_MS = 6000;
/** 'sent' when the write reaches the server, 'queued' when it is still waiting after SEND_WAIT_MS (rule 4). */
export function raceSend<T>(write: Promise<T>): Promise<'sent' | 'queued'> {
  return Promise.race([write.then(() => 'sent' as const), new Promise<'queued'>(r => setTimeout(() => r('queued'), SEND_WAIT_MS))]);
}

/** File a draft: server time, device time, location, number (rule 4). Returns whether the server has it yet. */
export async function fileRecordDoc(ref: DocumentReference, patch: Record<string, unknown>, number: string | null): Promise<{ location: Pos | null; sent: 'sent' | 'queued' }> {
  const location = await getPosition(8000);
  const sent = await raceSend(updateDoc(ref, { ...patch, status: 'filed', filedAt: serverTimestamp(), filedAtDevice: Timestamp.fromMillis(Date.now()), location, number }));
  return { location, sent };
}

/** Void a filed record: office only, with a reason; the record stays (rule 2). */
export async function voidRecordDoc(ref: DocumentReference, reason: string, by: { uid: string; name: string }) {
  await updateDoc(ref, { status: 'void', voidedAt: serverTimestamp(), voidedBy: by, voidReason: reason.trim() });
}

/** Discard a draft and the subcollections it owns (rule 2). Rules allow this only while it is a draft. */
export async function discardRecordDoc(ref: DocumentReference, subcollections: string[] = ['signatures']) {
  for (const sub of subcollections) {
    const docs = await getDocs(collection(db, `${ref.path}/${sub}`));
    await Promise.all(docs.docs.map(d => deleteDoc(d.ref)));
  }
  await deleteDoc(ref);
}

/** Where a list row goes (rule 3): a draft to its editor (with ?r=id), anything else to its page. */
export function recordHref(r: Pick<BaseRecord, 'id' | 'status' | 'app'>, editorPath: string, pagePath: string, params: Record<string, string>): string {
  const q = new URLSearchParams(params);
  if (stateOf({ status: r.status, app: r.app, sentTo: null }) === 'draft') { q.set('r', r.id); return `${editorPath}?${q}`; }
  return `${pagePath.replace(':id', r.id)}?${q}`;
}

export const recordRef = (path: string) => doc(db, path);
